import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useViewMode } from '@/stores/viewMode'
import { PageHeader } from '@/components/PageHeader'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { CalendarIcon, Filter, X, Wifi, WifiOff, Download } from 'lucide-react'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useConnectionStatusSnapshots } from '@/hooks/useConnectionStatusSnapshots'
import { usePlaybackDurationReport } from '@/hooks/usePlaybackDurationReport'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

function formatUTCDateTime(isoString: string): string {
  const date = new Date(isoString)
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`
}

function formatDateParam(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function formatDurationSeconds(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function ConnectionSnapshotsTab() {
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)

  // Filters
  const [clinicId, setClinicId] = useState<string>('')
  const [broadcastDeviceId, setBroadcastDeviceId] = useState<string>('')
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()

  const { snapshots, pagination, isLoading, isError } = useConnectionStatusSnapshots({
    page,
    limit: pageSize,
    clinicId: clinicId ? Number(clinicId) : undefined,
    broadcastDeviceId: broadcastDeviceId ? Number(broadcastDeviceId) : undefined,
    startDate: startDate ? formatDateParam(startDate) : undefined,
    endDate: endDate ? formatDateParam(endDate) : undefined,
  })

  const clearFilters = () => {
    setClinicId('')
    setBroadcastDeviceId('')
    setStartDate(undefined)
    setEndDate(undefined)
    setPage(1)
  }

  const hasFilters = clinicId || broadcastDeviceId || startDate || endDate

  const generatePaginationItems = () => {
    if (!pagination) return []
    const items: (number | string)[] = []
    const { page: currentPage, totalPages } = pagination

    items.push(1)
    if (currentPage > 3) items.push('ellipsis')

    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      items.push(i)
    }

    if (currentPage < totalPages - 2) items.push('ellipsis')
    if (totalPages > 1) items.push(totalPages)

    return items
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-destructive">載入失敗，請稍後再試。</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="p-6">
        {/* Filters */}
        <div className="mb-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Filter className="h-4 w-4" />
              篩選條件
            </h3>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                清除篩選
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">診所 ID</label>
              <Input
                type="number"
                placeholder="輸入診所 ID"
                value={clinicId}
                onChange={(e) => {
                  setClinicId(e.target.value)
                  setPage(1)
                }}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">設備 ID</label>
              <Input
                type="number"
                placeholder="輸入設備 ID"
                value={broadcastDeviceId}
                onChange={(e) => {
                  setBroadcastDeviceId(e.target.value)
                  setPage(1)
                }}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">開始日期</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP', { locale: zhTW }) : '選擇日期'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date)
                      setPage(1)
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">結束日期</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP', { locale: zhTW }) : '選擇日期'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      setEndDate(date)
                      setPage(1)
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Loading */}
        {isLoading ? (
          <Skeleton className="h-[400px] w-full" />
        ) : (
          <>
            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">快照時間</th>
                      <th className="text-left p-3 font-medium">診所 ID</th>
                      <th className="text-left p-3 font-medium">設備 ID</th>
                      <th className="text-left p-3 font-medium">連線狀態</th>
                      <th className="text-left p-3 font-medium">最後連線時間</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshots && snapshots.length > 0 ? (
                      snapshots.map((snapshot) => (
                        <tr key={snapshot.snapshot_id} className="border-t hover:bg-muted/30">
                          <td className="p-3 text-sm font-mono">
                            {formatUTCDateTime(snapshot.snapshot_hour)}
                          </td>
                          <td className="p-3 text-sm">{snapshot.clinic_id}</td>
                          <td className="p-3 text-sm">{snapshot.broadcast_device_id}</td>
                          <td className="p-3">
                            {snapshot.is_connected ? (
                              <Badge className="bg-green-100 text-green-800">
                                <Wifi className="h-3 w-3 mr-1" />
                                在線
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800">
                                <WifiOff className="h-3 w-3 mr-1" />
                                離線
                              </Badge>
                            )}
                          </td>
                          <td className="p-3 text-sm font-mono text-muted-foreground">
                            {snapshot.last_connected_at
                              ? formatUTCDateTime(snapshot.last_connected_at)
                              : '-'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">
                          沒有符合條件的快照紀錄
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  共 {pagination.total} 筆，第 {pagination.page} / {pagination.totalPages} 頁
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setPage(Math.max(1, page - 1))}
                        className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>

                    {generatePaginationItems().map((item, index) =>
                      item === 'ellipsis' ? (
                        <PaginationItem key={`ellipsis-${index}`}>
                          <span className="px-3">...</span>
                        </PaginationItem>
                      ) : (
                        <PaginationItem key={item}>
                          <PaginationLink
                            onClick={() => setPage(Number(item))}
                            isActive={page === item}
                            className="cursor-pointer"
                          >
                            {item}
                          </PaginationLink>
                        </PaginationItem>
                      ),
                    )}

                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                        className={
                          page === pagination.totalPages
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

type RangePreset = '7' | '14' | '30' | 'custom'

function presetToRange(preset: RangePreset): { startDate: Date; endDate: Date } | null {
  if (preset === 'custom') return null
  const days = Number(preset)
  const endDate = new Date()
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - (days - 1))
  return { startDate, endDate }
}

function PlaybackDurationReportTab({ providerId }: { providerId?: number }) {
  const [preset, setPreset] = useState<RangePreset>('7')
  const initialRange = presetToRange('7')
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(initialRange?.startDate)
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(initialRange?.endDate)

  const activeRange =
    preset === 'custom'
      ? { startDate: customStartDate, endDate: customEndDate }
      : presetToRange(preset)

  const { report, isLoading, isError } = usePlaybackDurationReport({
    providerId,
    startDate: activeRange?.startDate ? formatDateParam(activeRange.startDate) : undefined,
    endDate: activeRange?.endDate ? formatDateParam(activeRange.endDate) : undefined,
  })

  const handleExport = () => {
    if (!report) return

    const header = ['診所 ID', '診所名稱', ...report.dates, '總計']
    const rows = report.clinics.map((clinic) => {
      const dayMap = new Map(clinic.days.map((d) => [d.date, d.duration_seconds]))
      return [
        clinic.clinic_id,
        clinic.clinic_name,
        ...report.dates.map((date) => formatDurationSeconds(dayMap.get(date) ?? 0)),
        formatDurationSeconds(clinic.total_duration_seconds),
      ]
    })

    const ws = XLSX.utils.aoa_to_sheet([header, ...rows])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, '播放時長紀錄')

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
    saveAs(blob, `播放時長紀錄_${report.startDate}_${report.endDate}.xlsx`)
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium mr-2">查詢區間</span>
              {(['7', '14', '30'] as RangePreset[]).map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant={preset === p ? 'default' : 'outline'}
                  onClick={() => setPreset(p)}
                >
                  近 {p} 天
                </Button>
              ))}
              <Button
                size="sm"
                variant={preset === 'custom' ? 'default' : 'outline'}
                onClick={() => setPreset('custom')}
              >
                自訂日期
              </Button>

              {preset === 'custom' && (
                <>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customStartDate
                          ? format(customStartDate, 'PPP', { locale: zhTW })
                          : '開始日期'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={customStartDate}
                        onSelect={setCustomStartDate}
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customEndDate ? format(customEndDate, 'PPP', { locale: zhTW }) : '結束日期'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={customEndDate}
                        onSelect={setCustomEndDate}
                      />
                    </PopoverContent>
                  </Popover>
                </>
              )}
            </div>

            <Button
              size="sm"
              onClick={handleExport}
              disabled={!report || report.clinics.length === 0}
            >
              <Download className="h-4 w-4 mr-1" />
              匯出 Excel
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="text-center text-destructive py-8">載入失敗，請稍後再試。</div>
        ) : isLoading ? (
          <Skeleton className="h-[400px] w-full" />
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium sticky left-0 bg-muted/50">診所</th>
                    {report?.dates.map((date) => (
                      <th key={date} className="text-left p-3 font-medium whitespace-nowrap">
                        {date}
                      </th>
                    ))}
                    <th className="text-left p-3 font-medium whitespace-nowrap">總計</th>
                  </tr>
                </thead>
                <tbody>
                  {report && report.clinics.length > 0 ? (
                    report.clinics.map((clinic) => {
                      const dayMap = new Map(clinic.days.map((d) => [d.date, d.duration_seconds]))
                      return (
                        <tr key={clinic.clinic_id} className="border-t hover:bg-muted/30">
                          <td className="p-3 text-sm sticky left-0 bg-background">
                            {clinic.clinic_name}
                            <span className="text-muted-foreground"> (#{clinic.clinic_id})</span>
                          </td>
                          {report.dates.map((date) => (
                            <td key={date} className="p-3 text-sm font-mono whitespace-nowrap">
                              {formatDurationSeconds(dayMap.get(date) ?? 0)}
                            </td>
                          ))}
                          <td className="p-3 text-sm font-mono font-semibold whitespace-nowrap">
                            {formatDurationSeconds(clinic.total_duration_seconds)}
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={(report?.dates.length ?? 0) + 2}
                        className="p-8 text-center text-muted-foreground"
                      >
                        沒有符合條件的播放時長紀錄
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default function ConnectionStatusSnapshotsPage() {
  const { mode, id } = useViewMode()
  const headerLevel = mode === 'admin' ? 'page' : 'section'
  const providerId = mode === 'provider' && id ? id : undefined

  return (
    <div className="space-y-6">
      <PageHeader
        title="連線狀態快照 / 播放時長紀錄"
        description="每小時設備連線狀態歷史紀錄，以及各診所每日播放時長報表"
        level={headerLevel}
      />

      <Tabs defaultValue="connection-status">
        <TabsList>
          <TabsTrigger value="connection-status">連線狀態快照</TabsTrigger>
          <TabsTrigger value="playback-duration">播放時長紀錄</TabsTrigger>
        </TabsList>
        <TabsContent value="connection-status">
          <ConnectionSnapshotsTab />
        </TabsContent>
        <TabsContent value="playback-duration">
          <PlaybackDurationReportTab providerId={providerId} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
